import {
  forwardRef,
  type CSSProperties,
  type ReactNode,
  type Ref,
  type ComponentProps,
} from 'react';
import * as React from 'react';
import clsx from 'clsx';
import styles from './TransparentButton.module.css';

type Props = {
  testid?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  type?: ComponentProps<'button'>['type'];
  ariaExpanded?: boolean;
  ariaControls?: string;
  children?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
} & {
  // Declarative analytics opt-in (see @hungryroot/segment trackableProps).
  // Only `data-analytics-*` attributes pass through; this component otherwise
  // stays a closed prop surface on purpose.
  [key: `data-analytics-${string}`]: string | undefined;
};

/**
 * Not technically a Ramen component. Putting here because it's used by Ramen (and prolifically across other packages).
 * This avoids circular dependencies.
 */
export const TransparentButton = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement>) => {
    const {
      type,
      ariaControls,
      ariaExpanded,
      testid,
      className,
      style,
      ariaLabel,
      children,
      onClick,
      autoFocus,
      disabled,
      onKeyDown,
    } = props;
    const analyticsAttributes: Record<string, string> = {};
    for (const [key, value] of Object.entries(props)) {
      if (key.startsWith('data-analytics-') && typeof value === 'string') {
        analyticsAttributes[key] = value;
      }
    }
    return (
      <button
        {...analyticsAttributes}
        type={type}
        ref={ref}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        autoFocus={autoFocus}
        data-testid={testid}
        className={clsx(className, styles.button)}
        style={style}
        onClick={onClick}
        aria-label={ariaLabel ?? ''}
        disabled={disabled}
        onKeyDown={onKeyDown}
      >
        {children}
      </button>
    );
  }
);
